import React, { useState, useRef, useEffect } from 'react'
import { Check, ChevronDown, Close } from '../../../assets/icons'
import { InputContainer, InputWrapper, Label, StyledInput, HelperText } from '../input/styled'
import { DropdownList, DropdownOption, ErrorMessage } from '../select/styled'
import { CheckListItemContent, HiddenCheckbox, StyledCheckbox } from '../checkList/styled'
import { getMultiDisplayText } from '../../../utils/stringsUtils'
import { AutocompleteWrapper, InputActions, ClearButton, ChevronButton } from './styled'

const Autocomplete = ({
	label,
	name,
	value,
	onChange,
	options = [],
	placeholder = '',
	error,
	disabled = false,
	required = false,
	helperText,
	assignFieldValue,
	openOnFocus = false,
	onOpenRequest,
	showButton = true,
	width,
	isMultiSelect = false,
	containerStyle = {},
	...props
}) => {
	const [isOpen, setIsOpen] = useState(false)
	const [inputValue, setInputValue] = useState('')
	const [focusedIndex, setFocusedIndex] = useState(-1)
	const [isWaitingForOptions, setIsWaitingForOptions] = useState(false)
	const wrapperRef = useRef(null)
	const inputRef = useRef(null)
	const isTyping = useRef(false)
	const isFocusedRef = useRef(false)

	useEffect(() => {
		if (isTyping.current) return
		if (isMultiSelect) {
			setInputValue(getMultiDisplayText(value, options))
			return
		}
		const selectedOption = options.find(opt => opt.value === value)
		const next = selectedOption
			? selectedOption.label
			: typeof value === 'string' || typeof value === 'number'
				? String(value)
				: ''
			if (inputValue !== next) setInputValue(next)
	}, [value, options])

	useEffect(() => {
		if (!isOpen) return
		const handleClickOutside = ({ target }) => {
			if (wrapperRef.current && !wrapperRef.current.contains(target)) {
				setIsOpen(false)
				setFocusedIndex(-1)
			}
		}
		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [isOpen])

	const filteredOptions = options.filter(option => {
		const optionLabel = option?.label?.toString() ?? ''
		const search = isMultiSelect ? (isTyping.current ? inputValue : '') : inputValue
		return optionLabel.toLowerCase().includes(search.toLowerCase())
	})

	useEffect(() => {
		if (!isWaitingForOptions || options.length === 0) return
		if (!isFocusedRef.current) return

		setIsOpen(true)
		setFocusedIndex(-1)
		setIsWaitingForOptions(false)
	}, [isWaitingForOptions, options])

	const requestDropdownOpen = () => {
		if (disabled || !onOpenRequest) return false

		const shouldWaitForOptions = onOpenRequest()

		if (shouldWaitForOptions) {
			setIsWaitingForOptions(true)
		}

		return shouldWaitForOptions
	}

	const handleInputChange = ({ target: { value: val } }) => {
		isTyping.current = true
		setInputValue(val)
		setIsOpen(true)
		setFocusedIndex(-1)
	}

	const handleOptionClick = option => {
		isTyping.current = false
		if (isMultiSelect) {
			const current = Array.isArray(value) ? value : []
			const newValues = current.includes(option.value)
				? current.filter(v => v !== option.value)
				: [...current, option.value]
			setInputValue(getMultiDisplayText(newValues, options))
			if (onChange) onChange({ target: { name, value: newValues } })
			return
		}
		setInputValue(option.label)
		setIsOpen(false)
		setFocusedIndex(-1)
		if (assignFieldValue) {
			assignFieldValue(option.value)
		} else if (onChange) {
			onChange({ target: { name, value: option.value } })
		}
	}

	const handleClear = e => {
		e.preventDefault()
		e.stopPropagation()
		isTyping.current = false
		setInputValue('')
		setIsOpen(false)
		setFocusedIndex(-1)
		if (isMultiSelect) {
			if (onChange) onChange({ target: { name, value: [] } })
			return
		}
		if (assignFieldValue) {
			assignFieldValue('')
		} else if (onChange) {
			onChange({ target: { name, value: '' } })
		}
		if (document.activeElement) document.activeElement.blur()
	}

	const handleToggleDropdown = e => {
		e.preventDefault()
		e.stopPropagation()
		if (disabled) return

		if (isOpen) {
			setIsOpen(false)
			setFocusedIndex(-1)
			return
		}

		if (inputRef.current) {
			inputRef.current.focus()
		}

		const shouldWaitForOptions = requestDropdownOpen()

		if (shouldWaitForOptions) {
			setFocusedIndex(-1)
			return
		}

		setIsOpen(true)
		setFocusedIndex(-1)
	}

	const handleFocus = () => {
		isFocusedRef.current = true
		if (isMultiSelect) {
			isTyping.current = true
			setInputValue('')
			setIsOpen(true)
			return
		}
		if (openOnFocus && requestDropdownOpen()) {
			setFocusedIndex(-1)
			return
		}
		if (filteredOptions.length > 0) setIsOpen(true)
	}

	const handleBlur = () => {
		isFocusedRef.current = false
		isTyping.current = false
		setTimeout(() => {
			if (isMultiSelect) setInputValue(getMultiDisplayText(value, options))
			setIsOpen(false)
		}, 120)
	}

	const handleKeyDown = e => {
		if (!isOpen) return
		switch (e.key) {
			case 'ArrowDown':
				e.preventDefault()
				setFocusedIndex(prev => (prev < filteredOptions.length - 1 ? prev + 1 : 0))
				break
			case 'ArrowUp':
				e.preventDefault()
				setFocusedIndex(prev => (prev > 0 ? prev - 1 : filteredOptions.length - 1))
				break
			case 'Enter':
				if (focusedIndex >= 0) {
					e.preventDefault()
					handleOptionClick(filteredOptions[focusedIndex])
				}
				break
			case 'Escape':
				setIsOpen(false)
				setFocusedIndex(-1)
				break
			default:
				break
		}
	}

	return (
		<AutocompleteWrapper innerRef={wrapperRef} $width={width} style={containerStyle}>
			<InputWrapper style={{ width: '100%' }}>
			{label && (
				<Label htmlFor={name}>
					{label}
					{required && <span className="req">*</span>}
				</Label>
			)}
			<InputContainer style={{ position: 'relative' }} width="100%">
				<StyledInput
					width="100%"
					innerRef={inputRef}
					id={name}
					name={name}
					value={inputValue}
					placeholder={placeholder}
					onChange={handleInputChange}
					onFocus={handleFocus}
					onBlur={handleBlur}
					onKeyDown={handleKeyDown}
					disabled={disabled}
					autoComplete="off"
					$hasRight
					$error={!!error}
					style={{ paddingRight: showButton ? 72 : undefined }}
					{...props}
				/>
					<InputActions>
					{showButton && (isMultiSelect ? (Array.isArray(value) && value.length > 0) : !!inputValue) && !disabled && (
						<ClearButton onMouseDown={handleClear} aria-label="Limpiar selección">
							<Close/>
						</ClearButton>
					)}
					<ChevronButton $isOpen={isOpen} $disabled={disabled} onMouseDown={handleToggleDropdown}>
						<ChevronDown />
					</ChevronButton>
				</InputActions>
				{isOpen && (
					<DropdownList style={{ width: '100%' }}>
						{filteredOptions.length === 0 ? (
							<DropdownOption disabled>Sin resultados</DropdownOption>
						) : (
							filteredOptions.map((option, idx) => {
								const isChecked = isMultiSelect
									? (Array.isArray(value) && value.includes(option.value))
									: false
								const isFocused = idx === focusedIndex
								return (
									<DropdownOption
										key={option.value}
										onMouseDown={e => {
											e.preventDefault()
											handleOptionClick(option)
										}}
										$isSelected={isMultiSelect ? isChecked : isFocused}
										style={(!isMultiSelect && isFocused) ? { background: '#e0e0e0', fontWeight: 600 } : {}}
										tabIndex={-1}
									>
										{isMultiSelect ? (
											<CheckListItemContent>
												<HiddenCheckbox
													checked={isChecked}
													onChange={() => {}}
													onClick={e => e.stopPropagation()}
												/>
												<StyledCheckbox $checked={isChecked}>
													{isChecked && <Check />}
												</StyledCheckbox>
												<span>{option.label}</span>
											</CheckListItemContent>
										) : (
											option.label
										)}
									</DropdownOption>
								)
							})
						)}
					</DropdownList>
				)}
			</InputContainer>
			{error && <ErrorMessage>{error}</ErrorMessage>}

			{helperText && <HelperText>{helperText}</HelperText>}
			</InputWrapper>
		</AutocompleteWrapper>
	)
}

export default Autocomplete
